import { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "react-auth0-spa";
import {
  Box,
  CircularProgress,
  Container,
  Link,
  Typography,
} from "@mui/material";
import Axios from "axios";
import { useCookies } from "react-cookie";
import Title from "./Components/Title";
import { CHECKOUT_ENDPOINT } from "Utils/Constants";
import { FreeTrialDocDto } from "../../types/Donate";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { CONTENTFUL_FREE_TRIAL_ENTRY_ID } from "Utils/Constants";
import ContentfulClient from "Utils/ContentfulClient";
import { useAsync } from "react-use";
import DefaultLayout from "Containers/Layout/DefaultLayout";

const messageNotElligible = "It looks like you are already a member.";

const ThankYouFreeTrial = (props: any) => {
  const { isAuthenticated, refreshUserSilently, user } = useAuth0();
  const [isFreeTrialApplied, setIsFreeTrialApplied] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [cookies, removeCookie] = useCookies([
    "freeTrialCoupon",
    "freeTrialVersion",
  ]);

  const freeTrialCouponCookie = cookies.freeTrialCoupon;
  const freeTrialVersionCookie = cookies.freeTrialVersion;

  const getCustomer = (userInfo, freeTrialVersion) => {
    return {
      first_name: userInfo?.given_name,
      last_name: userInfo?.family_name,
      email: userInfo?.email,
      auth0_id: userInfo?.sub,
      meta_data: { freeTrialVersion },
    };
  };

  const Loading = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "inherit",
        }}
      >
        <CircularProgress />
      </Box>
    );
  };

  useEffect(() => {
    refreshUserSilently();
    // eslint-disable-next-line
  }, []);

  const { value: entityDoc } = useAsync(async () => {
    const entry = await ContentfulClient.getEntry<FreeTrialDocDto>(
      CONTENTFUL_FREE_TRIAL_ENTRY_ID
    );
    const ret = {
      thankYouHeader: entry.fields.thankYouPageContentHeader,
      thankYouContent: documentToReactComponents(
        entry.fields.thankYouPageContent
      ),
    };
    return ret;
  }, []);

  const ThankYouHeader = useCallback(() => {
    return <>{entityDoc?.thankYouHeader || "Thank You"}</>;
  }, [entityDoc?.thankYouHeader]);

  useEffect(() => {
    // console.log("🚀 ~ ThankYouFreeTrial ~ isAuthenticated:", isAuthenticated);
    console.log("🚀 ~ ThankYouFreeTrial ~ user:", user);

    if (
      (user?.plan && !isFreeTrialApplied) ||
      ((!freeTrialCouponCookie || !freeTrialVersionCookie) &&
        !isFreeTrialApplied)
    ) {
      setIsLoading(false);
      setIsError(true);
    }

    if ((isFreeTrialApplied && user?.plan) || isError) {
      setIsLoading(false);
      return;
    }

    if (!isFreeTrialApplied && isAuthenticated && user) {
      setIsLoading(true);
      createFreeTrialPlan();
    }

    // @todo call API to add Free Trial to member
    async function createFreeTrialPlan() {
      try {
        setIsFreeTrialApplied(true);
        await Axios.post(
          process.env.REACT_APP_API_ENDPOINT + "/checkout/coupon",
          {
            coupon: freeTrialCouponCookie,
            plan: "",
            freeTrial: true,
          }
        );

        await Axios.post(
          process.env.REACT_APP_API_ENDPOINT + CHECKOUT_ENDPOINT + "/v2",
          {
            captcha: "abc123",
            customer: getCustomer(user, freeTrialVersionCookie),
            shippingAddress: {},
            amount: 1000,
            paymentMethod: {
              type: "freeTrial",
            },
            autoRenew: false,
            monthly: true,
            plan: null,
            coupon: freeTrialCouponCookie,
            shipping: "",
            gift: "",
            isGift: false,
            giftRecipient: {},
            dev: process.env.REACT_APP_NODE_ENV === "dev",
            password: undefined,
          }
        );

        setIsLoading(false);

        refreshUserSilently();

        removeCookie("freeTrialCoupon", "", { path: "/" });
        removeCookie("freeTrialVersion", "", { path: "/" });
      } catch (e: any) {
        // console.log("error occured: ", e);
        setIsError(true);
      }
    }
  }, [
    freeTrialCouponCookie,
    freeTrialVersionCookie,
    isAuthenticated,
    isError,
    isFreeTrialApplied,
    user,
    refreshUserSilently,
    removeCookie,
  ]);

  if (isLoading) {
    return (
      <DefaultLayout disablePlayer={isLoading}>
        <Container
          maxWidth="sm"
          sx={(theme) => ({ textAlign: "center", ...theme.mixins.pageContent })}
        >
          <Loading />
        </Container>
      </DefaultLayout>
    );
  }

  if (isError)
    return (
      <DefaultLayout>
        <Container
          maxWidth="sm"
          sx={(theme) => ({ textAlign: "center", ...theme.mixins.pageContent })}
        >
          An error has occured or you have already registered for the Free
          Trial, please{" "}
          <Link
            href="mailto: support@jazzgroove.org"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us
          </Link>{" "}
          for support.
        </Container>
      </DefaultLayout>
    );

  return (
    <DefaultLayout disablePlayer={isLoading}>
      <Container
        maxWidth="sm"
        sx={(theme) => ({ textAlign: "center", ...theme.mixins.pageContent })}
      >
        <Title value="Thank You" />
        <Typography variant="h1" sx={{ mb: 3 }}>
          <ThankYouHeader />
          {user?.given_name && `, ${user.given_name}`}!
        </Typography>
        {user?.plan &&
          !isFreeTrialApplied &&
          !user?.free_trial &&
          messageNotElligible}
        {!(user?.plan && !isFreeTrialApplied && !user?.free_trial) &&
          entityDoc &&
          entityDoc.thankYouContent}
      </Container>
    </DefaultLayout>
  );
};

export default ThankYouFreeTrial;
